import jsPDF from 'jspdf'

interface AssignmentData {
  id: number
  device: {
    name: string
    description?: string
    deviceType: string
    chargerId?: string
    batteryType?: string
    batteryCount?: number
  }
  employee: {
    name: string
    email: string
  }
  assignedOn: string
  approvedOn?: string
  notes?: string
  approvalNotes?: string
  employeeSignature?: string
}

export function generateAssignmentPDF(assignment: AssignmentData): void {
  // Validate assignment data
  if (!assignment) {
    console.error('Assignment data is required for PDF generation')
    return
  }

  if (!assignment.device) {
    console.error('Device data is required for PDF generation')
    return
  }

  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 25
  const contentWidth = pageWidth - (margin * 2)
  let yPosition = margin

  // Helper function to add text with proper spacing
  const addText = (text: string, x: number, y: number, options: any = {}) => {
    const { fontSize = 10, fontStyle = 'normal', align = 'left', maxWidth = contentWidth } = options
    doc.setFontSize(fontSize)
    doc.setFont('helvetica', fontStyle)
    
    if (maxWidth && maxWidth < contentWidth) {
      const lines = doc.splitTextToSize(text, maxWidth)
      doc.text(lines, x, y, { align })
      return y + (lines.length * (fontSize * 0.4))
    } else {
      doc.text(text, x, y, { align })
      return y + (fontSize * 0.4)
    }
  }

  // Helper function to add a section divider
  const addDivider = (y: number) => {
    doc.setLineWidth(0.5)
    doc.line(margin, y, pageWidth - margin, y)
    return y + 8
  }

  // Header Section
  doc.setFontSize(24)
  doc.setFont('helvetica', 'bold')
  doc.text('DEVICE ASSIGNMENT', pageWidth / 2, yPosition, { align: 'center' })
  yPosition += 12

  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.text(`Assignment ID: ${assignment.id}`, pageWidth / 2, yPosition, { align: 'center' })
  yPosition += 8

  doc.setFontSize(8)
  doc.setFont('helvetica', 'italic')
  doc.text(`Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, pageWidth / 2, yPosition, { align: 'center' })
  yPosition = addDivider(yPosition + 12)

  // Main Content - Two Column Layout
  const leftColumn = margin
  const rightColumn = margin + (contentWidth / 2) + 10

  // Left Column - Assignment Details
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  yPosition = addText('ASSIGNMENT DETAILS', leftColumn, yPosition + 5)
  
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  yPosition = addText(`Device: ${assignment.device.name || 'Unknown Device'}`, leftColumn, yPosition + 8)
  yPosition = addText(`Type: ${assignment.device.deviceType || 'Unknown Type'}`, leftColumn, yPosition + 2)
  yPosition = addText(`Assigned: ${new Date(assignment.assignedOn).toLocaleDateString()}`, leftColumn, yPosition + 2)
  
  if (assignment.approvedOn) {
    yPosition = addText(`Approved: ${new Date(assignment.approvedOn).toLocaleDateString()}`, leftColumn, yPosition + 2)
  }

  // Right Column - Employee Details
  const rightYStart = yPosition - 20 // Align with left column
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  addText('EMPLOYEE DETAILS', rightColumn, rightYStart + 5)
  
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  let rightY = addText(`Name: ${assignment.employee?.name || 'Unknown Employee'}`, rightColumn, rightYStart + 8)
  rightY = addText(`Email: ${assignment.employee?.email || 'No email provided'}`, rightColumn, rightY + 2)

  // Notes Section (if any)
  const maxY = Math.max(yPosition, rightY)
  yPosition = maxY + 15

  if (assignment.notes || assignment.approvalNotes) {
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    yPosition = addText('NOTES', leftColumn, yPosition + 5)
    
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    
    if (assignment.notes) {
      yPosition = addText(`Admin Notes: ${assignment.notes}`, leftColumn, yPosition + 8, { maxWidth: contentWidth })
    }
    
    if (assignment.approvalNotes) {
      yPosition = addText(`Employee Notes: ${assignment.approvalNotes}`, leftColumn, yPosition + 5, { maxWidth: contentWidth })
    }
  }

  yPosition = addDivider(yPosition + 10)

  // Signature Section
  if (assignment.employeeSignature) {
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    yPosition = addText('EMPLOYEE SIGNATURE', leftColumn, yPosition + 5)
    
    // Add signature image
    try {
      const img = new Image()
      img.onload = () => {
        const maxImgWidth = 120
        const maxImgHeight = 60
        const imgWidth = Math.min(maxImgWidth, img.width)
        const imgHeight = Math.min(maxImgHeight, (img.height * imgWidth) / img.width)
        
        if (assignment.employeeSignature) {
          doc.addImage(assignment.employeeSignature, 'PNG', leftColumn, yPosition + 8, imgWidth, imgHeight)
        }
        
        // Add signature line
        doc.setLineWidth(0.5)
        doc.line(leftColumn, yPosition + 8 + imgHeight + 15, leftColumn + 150, yPosition + 8 + imgHeight + 15)
        
        doc.setFontSize(8)
        doc.setFont('helvetica', 'italic')
        doc.text('Employee Signature', leftColumn, yPosition + 8 + imgHeight + 20)
        
        // Save the PDF after adding the image
        doc.save(`device-assignment-${assignment.id}-${assignment.device.name.replace(/\s+/g, '-')}.pdf`)
      }
      img.onerror = () => {
        // Add signature placeholder
        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        yPosition = addText('Digital Signature Provided', leftColumn, yPosition + 8)
        
        // Add signature line
        doc.setLineWidth(0.5)
        doc.line(leftColumn, yPosition + 5, leftColumn + 150, yPosition + 5)
        
        doc.setFontSize(8)
        doc.setFont('helvetica', 'italic')
        doc.text('Employee Signature', leftColumn, yPosition + 10)
        
        // Save the PDF even if image fails
        doc.save(`device-assignment-${assignment.id}-${assignment.device.name.replace(/\s+/g, '-')}.pdf`)
      }
      img.src = assignment.employeeSignature
      return // Exit early since we're handling the save in the image load/error callbacks
    } catch (error) {
      // Add signature placeholder
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      yPosition = addText('Digital Signature Provided', leftColumn, yPosition + 8)
      
      // Add signature line
      doc.setLineWidth(0.5)
      doc.line(leftColumn, yPosition + 5, leftColumn + 150, yPosition + 5)
      
      doc.setFontSize(8)
      doc.setFont('helvetica', 'italic')
      doc.text('Employee Signature', leftColumn, yPosition + 10)
    }
  } else {
    // Add signature line even without signature
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    yPosition = addText('EMPLOYEE SIGNATURE', leftColumn, yPosition + 5)
    
    doc.setLineWidth(0.5)
    doc.line(leftColumn, yPosition + 8, leftColumn + 150, yPosition + 8)
    
    doc.setFontSize(8)
    doc.setFont('helvetica', 'italic')
    doc.text('Employee Signature', leftColumn, yPosition + 12)
  }

  // Footer
  const footerY = pageHeight - 25
  doc.setLineWidth(0.3)
  doc.line(margin, footerY - 15, pageWidth - margin, footerY - 15)
  
  doc.setFontSize(7)
  doc.setFont('helvetica', 'italic')
  doc.text('This document was generated automatically by the Device Management System', pageWidth / 2, footerY - 8, { align: 'center' })
  doc.text('For any queries, please contact the system administrator', pageWidth / 2, footerY - 2, { align: 'center' })

  // Save the PDF (only if no signature was processed)
  if (!assignment.employeeSignature) {
    doc.save(`device-assignment-${assignment.id}-${assignment.device.name.replace(/\s+/g, '-')}.pdf`)
  }
}

// Install jsPDF if not already installed
export function installJSPDF() {
  return import('jspdf').catch(() => {
    console.error('jsPDF is not installed. Please install it: npm install jspdf')
  })
}
